import {ref} from 'vue'
import {changeArticleStatus, deleteArticle, FindArticleDocument, postArticle,} from '/@src/api/contentRepo'
import {ContentLevel, MediaFile, RawArticle, TagData,} from '/@src/models/content'
import useApi, {useApollo} from '/@src/composable/useApi'
import {Article, Content, Exact, File, Scalars, TagRelation,} from '/@src/models/graphApi'
import {ContentKind, ContentStatus, TagGroup, TagID} from '/@src/models/api'
import {ArticleForm} from '/@src/models/form'
import _ from 'lodash'
import useContentBase from '/@src/composable/content/useContentBase'
import useUserSession from '/@src/composable/useUserSession'
import useTagRelations from '/@src/composable/content/useTagRelations'
import {getFileTypeBySuffix} from '/@src/utils/contentUtils'
import {MediaType} from '/@src/models/media'
import { convertRelationsKind, excludeTypename } from '/@src/utils'

export type FindArticleQuery = {
  __typename?: 'query'
  article?:
    | {
        __typename?: 'Article'
        id?: number | null | undefined
        title?: string | null | undefined
        summary?: string | null | undefined
        body?: string | null | undefined
        preview?: string | null | undefined
        cover_url?: string | null | undefined
        display_time?: any | null | undefined
        free_at?: any | null | undefined
        free_expired_at?: any | null | undefined
        is_cooperation?: boolean | null | undefined
        planned_published_at?: any | null | undefined
        source_url?: string | null | undefined
        status?: string | null | undefined
        vip_visibility?: number | null | undefined
        words_count?: number | null | undefined
        author?:
          | {
              __typename?: 'Author'
              id?: number | null | undefined
              nickname?: string | null | undefined
              avatar?: string | null | undefined
              description?: string | null | undefined
            }
          | null
          | undefined
        tags?:
          | Array<
              | {
                  __typename?: 'Tag'
                  id?: number | null | undefined
                  name?: string | null | undefined
                  description?: string | null | undefined
                  cover_url?: string | null | undefined
                }
              | null
              | undefined
            >
          | null
          | undefined
        tag_relations?:
          | Array<
              | { __typename?: 'TagRelation'; id: number; group: number }
              | null
              | undefined
            >
          | null
          | undefined
        attachments?:
          | Array<
              | {
                  __typename?: 'File'
                  title?: string | null | undefined
                  file_url?: string | null | undefined
                }
              | null
              | undefined
            >
          | null
          | undefined
      }
    | null
    | undefined
}
export type FindArticleQueryVariables = Exact<{
  id: Scalars['Int']
}>
export default function useArticle() {
  const api = useApi()
  const apollo = useApollo()
  const { user } = useUserSession()
  const articleResponse = ref<RawArticle>()
  // @ts-ignore
  const articleForm = ref<ArticleForm>({
    isFeatured: false,
    isSchedulePublish: false,
    //默认原创
    isOriginal: true
  })
  articleForm.value.content = {
    is_cooperation: false,
    author: {
      id: user.id,
      nickname: user.nickname,
      avatar: user.avatar,
    },
  }
  const { updateContentForm } = useContentBase()
  const { updateTagsWithRelations, setupTagRelations,has } = useTagRelations()
  const getArticle = async (variables: FindArticleQueryVariables) => {
    const ret = await apollo.query<FindArticleQuery, FindArticleQueryVariables>(
      { query: FindArticleDocument, variables: variables }
    )
    if (!ret.data.article) {
      throw new Error()
    }
    const content = _.cloneDeep(ret.data.article) as Article
    content.tags?.forEach((it) => {
      it!.select = true
    })
    articleForm.value = {
      id: ret.data.article!!.id,
      isLatest: !content.is_cooperation,
      content: content,
      tags: content.tags as TagData[],
      tagRelations: content.tag_relations as TagRelation[],
      isSchedulePublish: content.planned_published_at ? true : false,
      displayTime: content.display_time
        ? new Date(content.display_time)
        : undefined,
      scheduleTime: content.planned_published_at
        ? new Date(content.planned_published_at)
        : undefined,
      freeExpireAt: content.free_expired_at
        ? new Date(content.free_expired_at)
        : undefined,
      freeAfter: content.free_at ? new Date(content.free_at) : undefined,
    }
    if (content.tag_relations) {
      // 是否原创
      articleForm.value.isOriginal = has(content.tag_relations as TagRelation[],TagID.MATRIX,TagGroup.ORIGINAL_CONTENT)
    }
    // 转为小写状态值
    articleForm.value!!.content.status =
      articleForm.value.content.status?.toLowerCase()
    if (
      ret.data.article!!.attachments &&
      ret.data.article!!.attachments.length > 0
    ) {
      articleForm.value!!.attachmentMedias = []
      ret.data.article!!.attachments.forEach((it) => {
        articleForm.value!!.attachmentMedias!!.push({
          title: it!.title!,
          file_uri: it!.file_url!,
        })
      })
    }
    articleForm.value.tags = updateTagsWithRelations(
      articleForm.value.tagRelations,
      articleForm.value.tags
    )
    updateContentForm(articleForm.value!)
    return ret
  }
  const postArticleForm = (
    form: ArticleForm,
    isPublish: boolean,
    kind: ContentKind = ContentKind.ARTICLE
  ) => {
    if (isPublish) {
      // 如果当前文章未发布且设置定时发布，则状态为草稿
      if (
        form.content.status !== ContentStatus.PUBLISHED &&
        form.scheduleTime &&
        form.scheduleTime.getTime() > new Date().getTime()
      ) {
        form.content.status = ContentStatus.DRAFT
      } else {
        // 发布文章，状态强制改成已发布
        form.content.status = ContentStatus.PUBLISHED
      }
    } else {
      form.content.status = ContentStatus.DRAFT
    }
    console.log(form.content)
    form.isLatest = !form.content?.is_cooperation && form.isLatest
    setupTagRelations(form, kind)
    const articleData: RawArticle = {
      id: form.content.id!!,
      author_id: form.content.author?.id!!,
      title: form.content.title!!,
      body_summary: form.content.summary!!,
      body: form.content.body!!,
      article_type: form.content.article_type || 'article',
      body_preview: form.content.preview,
      vip_visibility: form.content.vip_visibility as ContentLevel,
      cover_uri: form.content.cover_url,
      display_time: form.content.display_time,
      is_cooperation: form.content.is_cooperation!!,
      planned_publish_at: form.content.planned_published_at,
      //外链
      source_uri: form.content.source_url!!,
      status: form.content.status as ContentStatus,
      free_at: form.freeAfter?.toISOString(),
      free_expired_at: form.content.free_expired_at,
      tag_relations: form.content.tag_relations?.length! > 0 ? excludeTypename(convertRelationsKind(form.content.tag_relations as TagRelation[])) : undefined,
      attachments: form.attachmentMedias,
    }
    if (kind === ContentKind.ARTICLE) {
      if (form.isFeatured || !form.content.is_cooperation) {
        articleData.is_featured = true
        articleData.is_front = true
      } else {
        articleData.is_featured = false
        articleData.is_front = false
      }
    } else if (kind === ContentKind.CALENDAR) {
      // 日历暂时强制发原来首页，兼容app
      if (form.isLatest) {
        articleData.is_front = true
      }
      if (form.attachmentMedias && form.attachmentMedias.length > 0) {
        const images = new Array<string>()
        form.attachmentMedias.forEach((it) => {
          console.log(it)
          switch (getFileTypeBySuffix(it!.file_extension!)) {
            case MediaType.PICTURE:
              images.push(it.file_uri||it.file_url)
              break
          }
        })
        articleData.cover_slide_uris = images
      }
    }
    return postArticle(api, articleData).then((res) => {
      articleForm.value.id = res.id
      articleForm.value.content.id = res.id
      return res
    })
  }
  const mapperMediaFiles = (files: File[]) => {
    const array: MediaFile[] = []
    files.map((it) => {
      array.push({
        file_uri: it.file_url!,
      })
    })
  }
  const mapperArticleContent = (article: RawArticle): Content => {
    // 兼容新老model
    ;(article as Article).source_url = article.source_uri
    return {
      raw_id: article.id,
      tags: article.tags,
      title: article.title,
      detail: article as Article,
      author: article.author,
      //注意大小写
      status: article.status,
      datetime: article.display_time,
      kind: ContentKind.ARTICLE,
    }
  }
  const deleteArticleContent = (id: number) => {
    return deleteArticle(api, id)
  }
  const setArticleStatus = (id: number, status: ContentStatus) => {
    return changeArticleStatus(api, id, status)
  }
  const isScheduleReleaseValid = (date:Date) => {
    if (date) {
      return date > new Date()
    } else {
      return false
    }
  }
  return {
    articleResponse,
    articleForm,
    isScheduleReleaseValid,
    mapperArticleContent,
    getArticle,
    setArticleStatus,
    postArticleForm,
    deleteArticleContent,
  }
}
